import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import CompanyDataForm from './components/CompanyDataForm';
import ProductEntryTable from './components/ProductEntryTable';
import BulkImportModal from './components/BulkImportModal';
import FormActions from './components/FormActions';
import { CompanyData, Product, ValidationError, FormState } from './types';

const NewTaxConsultationForm = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showAutoSaveNotification, setShowAutoSaveNotification] = useState(false);

  const [formState, setFormState] = useState<FormState>({
    companyData: {
      cnpj: '',
      ufOrigin: '',
      ufDestination: '',
      cfop: '',
      taxCharacteristics: '',
      purpose: '',
      taxRegime: ''
    },
    products: [],
    validationErrors: [],
    isSubmitting: false,
    isSaving: false
  });

  useEffect(() => {
    const savedData = localStorage.getItem('taxConsultationDraft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormState(prev => ({
          ...prev,
          companyData: parsed.companyData || prev.companyData,
          products: parsed.products || prev.products
        }));
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (formState.products.length > 0 || formState.companyData.cnpj) {
        localStorage.setItem('taxConsultationDraft', JSON.stringify({
          companyData: formState.companyData,
          products: formState.products,
          savedAt: new Date().toISOString()
        }));
        setShowAutoSaveNotification(true);
        setTimeout(() => setShowAutoSaveNotification(false), 2000);
      }
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formState.companyData, formState.products]);

  const validateForm = (): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!formState.companyData.cnpj || formState.companyData.cnpj.replace(/\D/g, '').length !== 14) {
      errors.push({ field: 'cnpj', message: 'CNPJ inválido. Formato: 00.000.000/0000-00' });
    }

    if (!formState.companyData.ufOrigin) {
      errors.push({ field: 'ufOrigin', message: 'UF de origem é obrigatória' });
    }

    if (!formState.companyData.ufDestination) {
      errors.push({ field: 'ufDestination', message: 'UF de destino é obrigatória' });
    }

    if (!formState.companyData.cfop) {
      errors.push({ field: 'cfop', message: 'CFOP é obrigatório' });
    }

    if (!formState.companyData.purpose) {
      errors.push({ field: 'purpose', message: 'Finalidade é obrigatória' });
    }

    if (!formState.companyData.taxRegime) {
      errors.push({ field: 'taxRegime', message: 'Regime tributário é obrigatório' });
    }

    formState.products.forEach((product) => {
      if (!product.code) {
        errors.push({ field: `${product.id}.code`, message: 'Código obrigatório' });
      }

      if (!product.description) {
        errors.push({ field: `${product.id}.description`, message: 'Descrição obrigatória' });
      }

      if (!product.ncm || product.ncm.replace(/\D/g, '').length !== 8) {
        errors.push({ field: `${product.id}.ncm`, message: 'NCM inválido (8 dígitos)' });
      }

      if (product.cest && product.cest.replace(/\D/g, '').length !== 7) {
        errors.push({ field: `${product.id}.cest`, message: 'CEST inválido (7 dígitos)' });
      }

      if (!product.quantity || product.quantity < 1) {
        errors.push({ field: `${product.id}.quantity`, message: 'Quantidade inválida' });
      }
    });

    const duplicateCodes = formState.products
      .map(p => p.code)
      .filter((code, index, arr) => code && arr.indexOf(code) !== index);

    duplicateCodes.forEach(code => {
      const product = formState.products.find(p => p.code === code);
      if (product) {
        errors.push({ field: `${product.id}.code`, message: 'Código duplicado' });
      }
    });

    return errors;
  };

  const handleCompanyDataChange = (data: CompanyData) => {
    setFormState(prev => ({
      ...prev,
      companyData: data,
      validationErrors: prev.validationErrors.filter(e => !e.field.startsWith('company'))
    }));
  };

  const handleProductsChange = (products: Product[]) => {
    setFormState(prev => ({
      ...prev,
      products,
      validationErrors: prev.validationErrors.filter(e => !products.some(p => e.field.startsWith(p.id)))
    }));
  };

  const handleBulkImport = (importedProducts: Product[]) => {
    const currentProducts = formState.products;
    const combinedProducts = [...currentProducts, ...importedProducts];
    
    if (combinedProducts.length > 300) {
      alert(`Limite de 300 produtos excedido. Apenas os primeiros 300 produtos serão importados.`);
      handleProductsChange(combinedProducts.slice(0, 300));
    } else {
      handleProductsChange(combinedProducts);
    }
    
    setShowBulkImport(false);
  };

  const handleSave = async () => {
    setFormState(prev => ({ ...prev, isSaving: true }));
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      localStorage.setItem('taxConsultationDraft', JSON.stringify({
        companyData: formState.companyData,
        products: formState.products,
        savedAt: new Date().toISOString()
      }));

      alert('Rascunho salvo com sucesso!');
    } catch (error) {
      alert('Erro ao salvar rascunho. Tente novamente.');
    } finally {
      setFormState(prev => ({ ...prev, isSaving: false }));
    }
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      setFormState(prev => ({ ...prev, validationErrors: errors }));
      alert(`Formulário contém ${errors.length} erro(s). Corrija os campos destacados.`);
      return;
    }

    setFormState(prev => ({ ...prev, isSubmitting: true, validationErrors: [] }));

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const consultationData = {
        id: `CONS-${Date.now()}`,
        companyData: formState.companyData,
        products: formState.products,
        submittedAt: new Date().toISOString(),
        status: 'processing'
      };

      localStorage.removeItem('taxConsultationDraft');
      
      navigate('/tax-calculation-results', { 
        state: { consultationData }
      });
    } catch (error) {
      alert('Erro ao enviar consulta. Tente novamente.');
      setFormState(prev => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleCancel = () => {
    if (formState.products.length > 0 || formState.companyData.cnpj) {
      if (window.confirm('Deseja descartar as alterações não salvas?')) {
        localStorage.removeItem('taxConsultationDraft');
        navigate('/tax-consultation-dashboard');
      }
    } else {
      navigate('/tax-consultation-dashboard');
    }
  };

  const isFormValid = () => {
    return (
      formState.companyData.cnpj &&
      formState.companyData.ufOrigin &&
      formState.companyData.ufDestination &&
      formState.companyData.cfop &&
      formState.companyData.purpose &&
      formState.companyData.taxRegime
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onCollapsedChange={setSidebarCollapsed}
      />
      
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <Header 
          title="Nova Consulta Tributária" 
          onUserMenuClick={() => {}} 
        />
        
        <main className="pt-16">
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <button
                  onClick={() => navigate('/tax-consultation-dashboard')}
                  className="hover:text-foreground transition-colors"
                >
                  Dashboard
                </button>
                <Icon name="ChevronRight" size={16} color="currentColor" />
                <span className="text-foreground">Nova Consulta</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Nova Consulta Tributária</h1>
                  <p className="text-muted-foreground mt-1">
                    Preencha os dados da empresa e adicione os produtos para consulta
                  </p>
                </div>
                
                {showAutoSaveNotification && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-md">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span className="text-sm text-success-foreground">Salvo automaticamente</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-card rounded-lg border border-border p-6 sticky top-24">
                  <CompanyDataForm
                    data={formState.companyData}
                    onChange={handleCompanyDataChange}
                    errors={formState.validationErrors}
                  />
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-card rounded-lg border border-border p-6">
                  <ProductEntryTable
                    products={formState.products}
                    onChange={handleProductsChange}
                    errors={formState.validationErrors}
                    onBulkImport={() => setShowBulkImport(true)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-card border-t border-border shadow-lg">
            <FormActions
              onSave={handleSave}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSaving={formState.isSaving}
              isSubmitting={formState.isSubmitting}
              hasProducts={formState.products.length > 0}
              isValid={isFormValid()}
            />
          </div>
        </main>
      </div>

      <BulkImportModal
        isOpen={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImport={handleBulkImport}
      />
    </div>
  );
};

export default NewTaxConsultationForm;